import { UserLookupContext } from '../components/UserLookupContext';
import { useUserLookupStore, userLookupFn } from '../store/use-user-lookup-store';
import React, { useCallback } from 'react';
import memoize from 'lodash/memoize';
import { isChannelStreamId, isSpaceStreamId, spaceIdFromChannelId } from '@towns-protocol/sdk';
/**
 * returns a function to imperatively lookup a user by userId within the scope of
 * the current space or channel
 */
export const useUserLookupContext = () => {
    const context = React.useContext(UserLookupContext);
    const spaceId = context?.spaceId;
    const channelId = context?.channelId;
    const genericLookupUser = useUserLookupStore((s) => s.lookupUser);
    const lookupUser = useCallback((userId) => genericLookupUser(userId, spaceId, channelId) ??
        // if user is not found and `allowUknown` set return a stable unknown user
        getStableDefault(userId), [genericLookupUser, spaceId, channelId]);
    return {
        lookupUser,
    };
};
/**
 * retrieve a user within scope of the current channel or space
 */
export const useUserLookup = (userId) => {
    const { spaceId, channelId } = React.useContext(UserLookupContext) ?? {};
    return useUserLookupStore((s) => userLookupFn(s, userId, spaceId, channelId) ?? getStableDefault(userId));
};
/**
 * based on a list of userIds, creates an array of users from the current channel
 * or space
 *
 * tldr; when possible use useUserLookup() to focus on the updated user slice
 *
 * This is useful when you need to lookup multiple users as a whole (e.g a
 * coma separated list of names that needs to re-rendered fully when any of the
 * usernames changes)
 *
 * However if the elements of the list can be rendered
 * as independent components it would be recommended to lookup each user slice individually to
 * avoid full re-renders. @see useUserLookup()
 */
export const useUserLookupArray = (userIds, streamId) => {
    let spaceId;
    let channelId;
    if (streamId) {
        if (isChannelStreamId(streamId)) {
            spaceId = spaceIdFromChannelId(streamId);
        }
        else if (isSpaceStreamId(streamId)) {
            spaceId = streamId;
        }
        else {
            channelId = streamId;
        }
    }
    return useUserLookupStore((s) => {
        return userIds.reduce((acc, userId) => {
            acc.push(userLookupFn(s, userId, spaceId, channelId) ?? getStableDefault(userId));
            return acc;
        }, []);
    });
};
/**
 * based on a list of userIds, creates a map of users from the current channel
 * or space
 *
 * tldr; when possible use useUserLookup() to focus on the updated user slice
 *
 * Similar to useUserLookupArray but more interesting if the list of userIds is
 * prone to change frequently. With the map the lookup doesn't need to trigger
 * an update because of the order or the ids changes
 */
export const useUserLookupMap = (userIds) => {
    const { spaceId, channelId } = React.useContext(UserLookupContext) ?? {};
    return useUserLookupStore((s) => {
        return userIds.reduce((acc, userId) => acc.set(userId, userLookupFn(s, userId, spaceId, channelId) ?? getStableDefault(userId)), new Map());
    });
};
const getStableDefault = memoize((userId) => ({
    // note: pretty user and display names will be generated from userId
    // via getPrettyDisplayName
    userId,
    username: '',
    usernameConfirmed: false,
    usernameEncrypted: false,
    displayName: '',
    displayNameEncrypted: false,
}), (userId) => userId);
//# sourceMappingURL=use-user-lookup-context.js.map